import { EventQueueView } from '@mark43/cad-api';

/**
 * Shared core for local-first event-# autocomplete.
 *
 * Every *active* DISPATCH event the server would return for an event-number search is
 * already on the client in the event-queue-views map (bootstrapped `eventQueue: 'DISPATCH'`
 * with no filters and kept live off the `eventQueues` stream, which deletes cleared events
 * from the map). That map is the same population as the server's active search and is fresher
 * than Elasticsearch. These helpers filter it instantly and reconcile the server response —
 * which additionally covers CLEARED events — in an append-only way.
 *
 * The core is intentionally UI- and option-type-agnostic: callers supply a mapper from
 * `EventQueueView` to their own option type and an id accessor, so the command line
 * (`CliAutocompleteOption`) and the form pickers (`EventOption`) can both reuse it.
 *
 * Matching mirrors the backend as closely as possible: case-insensitive substring on the
 * event number (the server uses an ES `*query*` wildcard on `agency_event_number` only, not
 * CFS text), ordered with suffix matches first then by event number. See
 * AgencyEventService.autocomplete / ElasticCobaltCadTicketSearchRepository.autocomplete.
 *
 * `resolveEventAutocompleteSelectedId` below is the fix for the reverted attempt's core bug:
 * instead of correcting a raw list index with arithmetic after the fact (which silently breaks
 * if the underlying arrays don't have the exact shape assumed), it always re-derives the
 * selected event's position from its identity, in whatever array is about to be displayed. See
 * that function's doc comment for the invariant it enforces.
 */

/**
 * Strip a single leading '#'. The server matches and echoes the raw event number and adds
 * the '#' itself; the client `eventNumber` field may or may not carry the prefix, so we
 * normalize to be correct either way. Exported for use by option mappers.
 */
export function normalizeEventNumber(eventNumber: string): string {
    return eventNumber.replace(/^#/, '');
}

/**
 * Filter + order active event queue views to mirror the server's EVENT autocomplete.
 *
 * `query` is the user's search text and never contains the `#` marker. Returns `[]` for an
 * empty query, matching the server which requires a non-empty query for EVENT.
 */
export function matchEventQueueViewsByEventNumber(
    eventQueueViews: EventQueueView[],
    query: string
): EventQueueView[] {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return [];
    }

    const normalizedNumber = (view: EventQueueView) =>
        normalizeEventNumber(view.eventNumber).toLowerCase();

    const matches = eventQueueViews.filter((view) =>
        normalizedNumber(view).includes(normalizedQuery)
    );

    const byEventNumberAsc = (a: EventQueueView, b: EventQueueView) =>
        normalizedNumber(a).localeCompare(normalizedNumber(b));

    const endsWithQuery = (view: EventQueueView) => normalizedNumber(view).endsWith(normalizedQuery);

    // Suffix matches first, then the rest — each group sorted by event number ascending.
    const suffixMatches = matches.filter(endsWithQuery).sort(byEventNumberAsc);
    const otherMatches = matches.filter((view) => !endsWithQuery(view)).sort(byEventNumberAsc);

    return [...suffixMatches, ...otherMatches];
}

/**
 * Build the instant, client-only option list in logical best-first order (callers reverse it
 * for a bottom-anchored display). `mapEventQueueViewToOption` adapts each matched active
 * event to the caller's option type.
 */
export function getClientEventAutocompleteOptions<TOption>(
    eventQueueViews: EventQueueView[],
    query: string,
    mapEventQueueViewToOption: (view: EventQueueView) => TOption
): TOption[] {
    return matchEventQueueViewsByEventNumber(eventQueueViews, query).map(mapEventQueueViewToOption);
}

/**
 * Reconcile the instant client options with the server response.
 *
 * Client options keep their exact positions; server options not already represented (by
 * event id, via `getEventId`) are APPENDED — never interleaved or reordered — so the list
 * stays stable under the user's keyboard selection. The appended block surfaces CLEARED
 * events and any active event the client was missing.
 */
export function reconcileEventAutocompleteOptions<TOption>(
    clientOptions: TOption[],
    serverOptions: TOption[],
    getEventId: (option: TOption) => number
): TOption[] {
    const clientIds = new Set(clientOptions.map(getEventId));
    const serverOnly = serverOptions.filter((option) => !clientIds.has(getEventId(option)));
    return [...clientOptions, ...serverOnly];
}

/**
 * Per-query memo of whether a selection has been resolved yet. Callers keep one of these in a
 * ref (not React state — it must survive across the two result sets without triggering a
 * render itself) and thread it through `resolveEventAutocompleteSelectedId` /
 * `advanceEventAutocompleteSelectionMemo` below.
 */
export interface EventAutocompleteSelectionMemo {
    query: string;
    hasSelection: boolean;
}

export const INITIAL_EVENT_AUTOCOMPLETE_SELECTION_MEMO: EventAutocompleteSelectionMemo = {
    query: '',
    hasSelection: false,
};

/**
 * Decide which event id should be highlighted after applying `newOptions` (logical,
 * best-first order) for `query`.
 *
 * This is the "set selection exactly once per query" invariant, expressed as identity lookups
 * rather than index arithmetic:
 *
 * - If this is the first result set for `query` (a brand-new query, or the same query but
 *   nothing has resolved yet because the prior — local — set was empty): pick the first id of
 *   `newOptions`, i.e. whichever result set is first to be non-empty. Returns `null` if
 *   `newOptions` is also empty (nothing to select yet — the next set gets another chance).
 * - Otherwise, a selection already exists for this query. Re-derive it by identity: look up
 *   whichever option is *currently* selected (`previousOptions[previousSelectedIndex]`) and
 *   return its id unchanged. This is what makes the result immune to the previous attempt's
 *   bug — the caller doesn't need `newOptions` to share any positional relationship with
 *   `previousOptions` (they never do, once server-only rows are spliced in); the id survives
 *   the reconcile because it's looked up, not offset. It also means the currently selected
 *   event is preserved even if the user has since navigated away from the original pick (the
 *   store's own arrow-key handling moves `previousSelectedIndex`, and this function is
 *   deliberately blind to *how* it got there).
 */
export function resolveEventAutocompleteSelectedId<TOption, TId>({
    memo,
    query,
    previousOptions,
    previousSelectedIndex,
    newOptions,
    getEventId,
}: {
    memo: EventAutocompleteSelectionMemo;
    query: string;
    previousOptions: TOption[];
    previousSelectedIndex: number | undefined;
    newOptions: TOption[];
    getEventId: (option: TOption) => TId;
}): TId | null {
    const isFirstResultSetForQuery = memo.query !== query || !memo.hasSelection;
    if (isFirstResultSetForQuery) {
        return newOptions.length > 0 ? getEventId(newOptions[0]) : null;
    }

    const currentlySelected =
        typeof previousSelectedIndex === 'number' ? previousOptions[previousSelectedIndex] : undefined;
    return currentlySelected ? getEventId(currentlySelected) : null;
}

/**
 * Advance the memo after applying a result set for `query`. `hasResults` reflects whether
 * *this* set was non-empty — call this every time, even when the set is empty, so a later
 * (e.g. server) set for the same query is still recognized as the first-to-resolve.
 */
export function advanceEventAutocompleteSelectionMemo(
    prev: EventAutocompleteSelectionMemo,
    query: string,
    hasResults: boolean
): EventAutocompleteSelectionMemo {
    if (prev.query !== query) {
        return { query, hasSelection: hasResults };
    }
    if (!prev.hasSelection && hasResults) {
        return { query, hasSelection: true };
    }
    return prev;
}

/**
 * Find the display index of `id` in `options`, or `undefined` if absent (no selection).
 */
export function findIndexByEventId<TOption, TId>(
    options: TOption[],
    id: TId | null,
    getEventId: (option: TOption) => TId
): number | undefined {
    if (id === null) {
        return undefined;
    }
    const index = options.findIndex((option) => getEventId(option) === id);
    return index === -1 ? undefined : index;
}
